/*
 * spd-a11y.js - keep the keyboard out of aria-hidden content.
 *
 * Flickity cells, the cart drawer and the PhotoSwipe gallery all mark subtrees
 * aria-hidden but leave the links and buttons inside them tabbable, so keyboard
 * users land on content screen readers cannot see. WCAG 2.0 A / 2.2 A.
 *
 * Two cases, and they need different answers:
 *   offscreen containers (drawer, lightbox, pixel sandbox)
 *       -> inert, so nothing reaches them at all
 *   carousel cells that are still on screen and clickable
 *       -> tabindex="-1" only; inert would kill the mouse too, and 15 visible
 *          product cards on the homepage sit in this case
 *
 * Everything is recorded on a data attribute and put back when aria-hidden
 * clears, so the components that own these subtrees keep control of them.
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button,input,select,textarea,iframe,summary,[tabindex]:not([tabindex="-1"])';
  var supportsInert = 'inert' in HTMLElement.prototype;

  // Recent focus history, newest first. When a panel closes we need somewhere to
  // send the caret, and the right answer is whatever the user was on before they
  // went in - the cart button, the gallery thumbnail, the quick-shop trigger.
  var focusTrail = [];

  document.addEventListener('focusin', function (e) {
    if (e.target === focusTrail[0]) return;
    focusTrail.unshift(e.target);
    if (focusTrail.length > 6) focusTrail.length = 6;
  }, true);

  function returnFocus(from) {
    for (var i = 0; i < focusTrail.length; i++) {
      var c = focusTrail[i];
      if (!c || c === from || from.contains(c) || !c.isConnected) continue;
      if (c.closest('[aria-hidden="true"],[inert]')) continue;
      var r = c.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) continue;
      c.focus();
      if (document.activeElement === c) return true;
    }
    return false;
  }

  function perceivable(el) {
    var s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') return false;

    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;

    // Parked offscreen (the cart drawer keeps its Hulk forms at left:-99999px).
    // Measured against the document, not the viewport, so content that is
    // merely scrolled out of sight still counts as perceivable.
    if (r.right + window.pageXOffset <= 0) return false;
    if (r.bottom + window.pageYOffset <= 0) return false;

    return true;
  }

  function mute(el) {
    // Something that just went hidden must not keep the caret, or the drawer
    // never goes inert again because it still holds focus. Hand the caret back
    // to whatever opened this; dropping it on <body> would satisfy the audit but
    // leave a keyboard user tabbing from the top of the page again.
    var active = document.activeElement;
    if (active && active !== document.body && el.contains(active)) {
      if (!returnFocus(el)) active.blur();
    }

    if (!supportsInert || perceivable(el)) {
      var nodes = el.querySelectorAll(FOCUSABLE);
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n.hasAttribute('data-spd-tabindex')) continue;
        n.setAttribute('data-spd-tabindex', n.hasAttribute('tabindex') ? n.getAttribute('tabindex') : '');
        n.setAttribute('tabindex', '-1');
      }
    } else if (!el.inert) {
      el.inert = true;
      el.setAttribute('data-spd-inert', '');
    }
  }

  function restore(el) {
    if (el.hasAttribute('data-spd-inert')) {
      el.inert = false;
      el.removeAttribute('data-spd-inert');
    }
    var nodes = el.querySelectorAll('[data-spd-tabindex]');
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var prev = n.getAttribute('data-spd-tabindex');
      if (prev === '') {
        n.removeAttribute('tabindex');
      } else {
        n.setAttribute('tabindex', prev);
      }
      n.removeAttribute('data-spd-tabindex');
    }
  }

  function sync(el) {
    if (!el || el.nodeType !== 1) return;
    if (el.getAttribute('aria-hidden') === 'true') {
      if (el.querySelector(FOCUSABLE) || el.querySelector('[data-spd-tabindex]')) mute(el);
    } else {
      restore(el);
    }
  }

  function scan(root) {
    if (!root || root.nodeType !== 1) return;
    if (root.hasAttribute('aria-hidden')) sync(root);
    var nodes = root.querySelectorAll('[aria-hidden]');
    for (var i = 0; i < nodes.length; i++) sync(nodes[i]);
  }

  function start() {
    scan(document.body);

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'attributes') {
          sync(r.target);
        } else {
          for (var j = 0; j < r.addedNodes.length; j++) {
            var node = r.addedNodes[j];
            scan(node);
            // Apps inject into containers that are already aria-hidden - the
            // Hulk forms iframes arrive this way, long after the first pass.
            if (node.nodeType === 1) {
              var host = node.parentElement && node.parentElement.closest('[aria-hidden="true"]');
              if (host) sync(host);
            }
          }
        }
      }
    }).observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

(function () {
  // Hulk renders its forms in untitled iframes. Give each one a name.
  function titleFrames() {
    document.querySelectorAll('iframe[src*="hulkapps"]:not([title])').forEach(function (f) {
      f.setAttribute('title', 'Request form');
    });
  }
  // The theme opens submenus on hover and on Enter. Add Space and Arrow Down,
  // and close the menu when focus leaves it.
  function menuKeys() {
    document.querySelectorAll('[data-navmenu-parent]').forEach(function (li) {
      var link = li.querySelector('a.navmenu-link-parent');
      if (!link) return;
      link.addEventListener('keydown', function (e) {
        if (e.key !== ' ' && e.key !== 'ArrowDown') return;
        e.preventDefault();
        if (!li.classList.contains('open')) link.click();
        if (e.key === 'ArrowDown') {
          var first = li.querySelector('[data-navmenu-submenu] a');
          if (first) setTimeout(function () { first.focus(); }, 60);
        }
      });
      li.addEventListener('focusout', function (e) {
        if (!li.contains(e.relatedTarget)) li.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
      });
    });
  }
  function run() {
    titleFrames();
    menuKeys();
    new MutationObserver(titleFrames).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();

(function () {
  // Privacy notice under every form that collects details, wherever the form comes from.
  var spdNoticeText = 'We use the details you enter here to respond to your request. See our <a href="/pages/privacy-policy">Privacy Policy</a> for what we collect and why.';
  function notice() { var p = document.createElement('p'); p.className = 'spd-notice'; p.innerHTML = spdNoticeText; return p; }
  function addNotices() {
    document.querySelectorAll('form[action*="/contact"]').forEach(function (f) {
      if (f.closest('.spd-footer') || f.querySelector('.spd-notice')) return;
      var btn = f.querySelector('button[type="submit"], input[type="submit"]');
      if (btn) btn.parentNode.insertBefore(notice(), btn);
    });
    document.querySelectorAll('iframe[src*="hulkapps"]').forEach(function (fr) {
      if (fr.closest('.ccd__request-panel, .ccd__hulk-forms')) return;
      var host = fr.closest('.pxFormGenerator') || fr.parentElement;
      if (!host || (host.parentElement && host.parentElement.querySelector('.spd-notice'))) return;
      host.insertAdjacentElement('afterend', notice());
    });
  }
  function run() {
    if (!document.getElementById('spd-notice-style')) {
      var st = document.createElement('style'); st.id = 'spd-notice-style';
      st.textContent = '.spd-notice{margin:10px 0 0;font-size:12px;line-height:1.5;color:#4c5764}.spd-notice a{color:inherit;text-decoration:underline}';
      document.head.appendChild(st);
    }
    addNotices();
    new MutationObserver(addNotices).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run); else run();
})();
